import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAppUser } from '../../../hooks/useAppUser';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import dayjs from 'dayjs';
import { useApi } from '../../../hooks/useApi';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82ca9d'];

function MetricCard({ title, value, subtitle, icon, color = 'primary' }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="body2" gutterBottom sx={{
          color: "text.secondary"
        }}>
          {title}
        </Typography>
        <Typography variant="h4" component="div" color={`${color}.main`}>
          {value}
        </Typography>
        {subtitle && (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mt: 1
            }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function UsageDashboard() {
  const navigate = useNavigate();
  const api = useApi();
  const { user } = useAppUser();
  const [granularity, setGranularity] = useState('daily');
  const [range, setRange] = useState('30d');
  const [hiddenLines, setHiddenLines] = useState({});

  // Include user ID in query keys so cache is per-user
  const userId = user?.id || user?._id || user?.email;

  const { data: summary, isLoading: summaryLoading, refetch: refetchSummary } = useQuery({
    queryKey: ['usage-summary', userId],
    queryFn: () => api.get('/api/usage/summary'),
    enabled: !!userId,
  });

  const { data: trends, isLoading: trendsLoading, refetch: refetchTrends } = useQuery({
    queryKey: ['usage-trends', userId, granularity, range],
    queryFn: () => api.get(`/api/usage/trends/${granularity}`, { range }),
    enabled: !!userId,
  });

  const { data: breakdown, isLoading: breakdownLoading, refetch: refetchBreakdown } = useQuery({
    queryKey: ['usage-breakdown', userId],
    queryFn: () => api.get('/api/usage/costs/breakdown'),
    enabled: !!userId,
  });

  const { data: activity, isLoading: activityLoading, refetch: refetchActivity } = useQuery({
    queryKey: ['usage-activity', userId],
    queryFn: () => api.get('/api/usage/rooms/activity?limit=10'),
    enabled: !!userId,
  });

  const handleGranularityChange = (event, newGranularity) => {
    if (newGranularity !== null) {
      setGranularity(newGranularity);
    }
  };

  const handleRangeChange = (event, newRange) => {
    if (newRange !== null) {
      setRange(newRange);
      // Daily buckets across a full year are unreadable — default YTD to monthly.
      if (newRange === 'ytd' && granularity === 'daily') {
        setGranularity('monthly');
      }
    }
  };

  const handleLegendClick = (dataKey) => {
    setHiddenLines((prev) => ({
      ...prev,
      [dataKey]: !prev[dataKey],
    }));
  };

  const handleRefresh = () => {
    refetchSummary();
    refetchTrends();
    refetchBreakdown();
    refetchActivity();
  };

  // Transform trends data for charts
  const trendData = useMemo(() => {
    if (!trends?.rooms || !trends?.credits) return [];

    // Merge rooms and credits data by date
    const dateMap = new Map();

    trends.rooms.forEach(item => {
      dateMap.set(item._id, {
        date: item._id,
        rooms: item.count || 0,
        participants: item.participants || 0,
        participantMinutes: 0,
        recordingMinutes: 0,
        streamingMinutes: 0,
        transcriptionMinutes: 0,
        compositionMinutes: 0,
        cost: 0,
      });
    });

    trends.credits.forEach(item => {
      const existing = dateMap.get(item._id) || { date: item._id, rooms: 0, participants: 0 };
      dateMap.set(item._id, {
        ...existing,
        participantMinutes: Math.round(item.participantMins || 0),
        recordingMinutes: Math.round(item.recordingMins || 0),
        streamingMinutes: Math.round(item.streamingMins || 0),
        transcriptionMinutes: Math.round(item.transcriptionMins || 0),
        compositionMinutes: Math.round(item.compositionMins || 0),
        cost: item.totalCost || 0,
      });
    });

    return Array.from(dateMap.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [trends]);

  // Transform breakdown data for pie chart
  const pieData = useMemo(() => {
    if (!breakdown?.breakdown) return [];

    const data = [];
    const b = breakdown.breakdown;

    if (parseFloat(b.participants?.cost) > 0) {
      data.push({ name: 'Participants', value: parseFloat(b.participants.cost), minutes: b.participants.minutes });
    }
    if (parseFloat(b.recording?.cost) > 0) {
      data.push({ name: 'Recording', value: parseFloat(b.recording.cost), minutes: b.recording.minutes });
    }
    if (parseFloat(b.streaming?.cost) > 0) {
      data.push({ name: 'Streaming', value: parseFloat(b.streaming.cost), minutes: b.streaming.minutes });
    }
    if (parseFloat(b.transcription?.cost) > 0) {
      data.push({ name: 'Transcription', value: parseFloat(b.transcription.cost), minutes: b.transcription.minutes });
    }
    if (parseFloat(b.composition?.cost) > 0) {
      data.push({ name: 'Composition', value: parseFloat(b.composition.cost), minutes: b.composition.minutes });
    }
    if (parseFloat(b.storage?.cost) > 0) {
      data.push({ name: 'Storage', value: parseFloat(b.storage.cost), usedGB: b.storage.usedGB });
    }

    // If no costs yet, show placeholder
    if (data.length === 0) {
      data.push({ name: 'No usage yet', value: 0 });
    }

    return data;
  }, [breakdown]);

  const isLoading = summaryLoading || trendsLoading || breakdownLoading || activityLoading;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Extract values from summary response
  const monthData = summary?.month || {};
  const activeData = summary?.active || {};
  const creditsData = summary?.credits || {};

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Usage Dashboard
          </Typography>
          <Typography variant="body1" sx={{
            color: "text.secondary"
          }}>
            Monitor your SDK usage and costs
            {summary?.period && (
              <> - {dayjs(summary.period.start).format('MMM D')} to {dayjs(summary.period.end).format('MMM D, YYYY')}</>
            )}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
        >
          Refresh
        </Button>
      </Box>
      {/* Summary Cards - Current Month */}
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        This Month
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 2.4
          }}>
          <MetricCard
            title="Participant Minutes"
            value={(creditsData.participantMinutes || 0).toLocaleString()}
            subtitle={`${activeData.rooms || 0} active rooms now`}
            color="primary"
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 2.4
          }}>
          <MetricCard
            title="Total Rooms"
            value={(monthData.rooms || 0).toLocaleString()}
            subtitle={`${creditsData.recordingMinutes || 0} recording mins`}
            color="success"
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 2.4
          }}>
          <MetricCard
            title="Recordings"
            value={(monthData.recordings || 0).toLocaleString()}
            subtitle={`${activeData.recordings || 0} active now`}
            color="info"
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 2.4
          }}>
          <MetricCard
            title="Storage"
            value={`${(summary?.storage?.usedGB || 0).toFixed(2)} GB`}
            subtitle={`${summary?.storage?.freeGB || 10} GB free tier`}
            color="secondary"
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 2.4
          }}>
          <MetricCard
            title="Total Cost"
            value={`$${creditsData.totalCost || '0.00'}`}
            subtitle="This billing period"
            color="warning"
          />
        </Grid>
      </Grid>
      {/* Free Minutes Pool Section */}
      {summary?.freeMinutes && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Free Minutes Pool
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 2
            }}>
            All usage types share from your {summary.freeMinutes.totalAllocation.toLocaleString()} monthly free minutes.
            Pool resets on the 1st of each month.
          </Typography>

          {/* Main progress indicator */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2">
                {summary.freeMinutes.used.toLocaleString()} used
              </Typography>
              <Typography variant="body2">
                {summary.freeMinutes.remaining.toLocaleString()} remaining
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.min(summary.freeMinutes.percentUsed, 100)}
              color={summary.freeMinutes.percentUsed >= 100 ? 'error' :
                     summary.freeMinutes.percentUsed >= 80 ? 'warning' : 'primary'}
              sx={{ height: 12, borderRadius: 1 }}
            />
          </Box>

          {/* Usage breakdown by category */}
          <Typography variant="subtitle2" gutterBottom>Usage Breakdown</Typography>
          <Grid container spacing={1}>
            {[
              { label: 'Participant', value: creditsData.participantMinutes },
              { label: 'Recording', value: creditsData.recordingMinutes },
              { label: 'Streaming', value: creditsData.streamingMinutes },
              { label: 'Transcription', value: creditsData.transcriptionMinutes },
              { label: 'Composition', value: creditsData.compositionMinutes },
            ].filter(item => item.value > 0).map(item => (
              <Grid
                key={item.label}
                size={{
                  xs: 6,
                  sm: 4,
                  md: 2
                }}>
                <Typography variant="caption" sx={{
                  color: "text.secondary"
                }}>{item.label}</Typography>
                <Typography variant="body2">{item.value.toLocaleString()} mins</Typography>
              </Grid>
            ))}
          </Grid>

          {summary.freeMinutes.exceededBy > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              You've exceeded your free allocation by {summary.freeMinutes.exceededBy.toLocaleString()} minutes.
              Standard rates apply to overage.
            </Alert>
          )}
        </Paper>
      )}
      {/* Cost Breakdown Section */}
      {breakdown?.breakdown && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Cost Breakdown
          </Typography>
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 6,
                sm: 4,
                md: 2
              }}>
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h5" sx={{
                  color: "primary.main"
                }}>
                  {breakdown.breakdown.participants?.minutes || 0}
                </Typography>
                <Typography variant="body2" sx={{
                  color: "text.secondary"
                }}>
                  Participant Mins
                </Typography>
                <Typography variant="h6">
                  ${breakdown.breakdown.participants?.cost || '0.00'}
                </Typography>
              </Box>
            </Grid>
            <Grid
              size={{
                xs: 6,
                sm: 4,
                md: 2
              }}>
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h5" sx={{
                  color: "success.main"
                }}>
                  {breakdown.breakdown.recording?.minutes || 0}
                </Typography>
                <Typography variant="body2" sx={{
                  color: "text.secondary"
                }}>
                  Recording Mins
                </Typography>
                <Typography variant="h6">
                  ${breakdown.breakdown.recording?.cost || '0.00'}
                </Typography>
              </Box>
            </Grid>
            <Grid
              size={{
                xs: 6,
                sm: 4,
                md: 2
              }}>
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h5" sx={{
                  color: "info.main"
                }}>
                  {breakdown.breakdown.streaming?.minutes || 0}
                </Typography>
                <Typography variant="body2" sx={{
                  color: "text.secondary"
                }}>
                  Streaming Mins
                </Typography>
                <Typography variant="h6">
                  ${breakdown.breakdown.streaming?.cost || '0.00'}
                </Typography>
              </Box>
            </Grid>
            <Grid
              size={{
                xs: 6,
                sm: 4,
                md: 2
              }}>
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h5" sx={{
                  color: "secondary.main"
                }}>
                  {breakdown.breakdown.transcription?.minutes || 0}
                </Typography>
                <Typography variant="body2" sx={{
                  color: "text.secondary"
                }}>
                  Transcription Mins
                </Typography>
                <Typography variant="h6">
                  ${breakdown.breakdown.transcription?.cost || '0.00'}
                </Typography>
              </Box>
            </Grid>
            <Grid
              size={{
                xs: 6,
                sm: 4,
                md: 2
              }}>
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h5" sx={{
                  color: "warning.main"
                }}>
                  {breakdown.breakdown.composition?.minutes || 0}
                </Typography>
                <Typography variant="body2" sx={{
                  color: "text.secondary"
                }}>
                  Composition Mins
                </Typography>
                <Typography variant="h6">
                  ${breakdown.breakdown.composition?.cost || '0.00'}
                </Typography>
              </Box>
            </Grid>
            <Grid
              size={{
                xs: 6,
                sm: 4,
                md: 2
              }}>
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h5" sx={{
                  color: "info.main"
                }}>
                  {parseFloat(breakdown.breakdown.storage?.usedGB || 0).toFixed(2)} GB
                </Typography>
                <Typography variant="body2" sx={{
                  color: "text.secondary"
                }}>
                  Storage Used
                </Typography>
                <Typography variant="h6">
                  ${breakdown.breakdown.storage?.cost || '0.00'}
                </Typography>
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ textAlign: 'center', pt: 2, borderTop: 1, borderColor: 'divider', mt: 2 }}>
            <Typography variant="h4" sx={{
              color: "error.main"
            }}>
              ${breakdown.breakdown.total || '0.00'}
            </Typography>
            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>
              Total Cost This Period
            </Typography>
          </Box>
        </Paper>
      )}
      {/* Time Selector */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h6">Usage Trends</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <ToggleButtonGroup
            value={range}
            exclusive
            onChange={handleRangeChange}
            size="small"
          >
            <ToggleButton value="30d">30 Days</ToggleButton>
            <ToggleButton value="ytd">Year to Date</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            value={granularity}
            exclusive
            onChange={handleGranularityChange}
            size="small"
          >
            <ToggleButton value="daily">Daily</ToggleButton>
            <ToggleButton value="weekly">Weekly</ToggleButton>
            <ToggleButton value="monthly">Monthly</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>
      <Grid container spacing={3}>
        {/* Usage Trends Chart */}
        <Grid
          size={{
            xs: 12,
            md: 8
          }}>
          <Paper sx={{ p: 3, height: 400 }}>
            <Typography variant="subtitle1" gutterBottom sx={{
              color: "text.secondary"
            }}>
              Usage Trends Over Time
            </Typography>
            {trendData.length > 0 ? (
              (() => {
                const allLines = [
                  { dataKey: 'participantMinutes', name: 'Participant Mins', stroke: '#8884d8', yAxisId: 'left' },
                  { dataKey: 'rooms', name: 'Rooms', stroke: '#82ca9d', yAxisId: 'right' },
                  { dataKey: 'recordingMinutes', name: 'Recording Mins', stroke: '#ffc658', yAxisId: 'right' },
                  { dataKey: 'streamingMinutes', name: 'Streaming Mins', stroke: '#00bcd4', yAxisId: 'right' },
                  { dataKey: 'transcriptionMinutes', name: 'Transcription Mins', stroke: '#e91e63', yAxisId: 'right' },
                  { dataKey: 'compositionMinutes', name: 'Composition Mins', stroke: '#ff7300', yAxisId: 'right' },
                ];

                // Only show lines that have data (aggregate > 0)
                const lines = allLines.filter((line) => {
                  const total = trendData.reduce((sum, item) => sum + (item[line.dataKey] || 0), 0);
                  return total > 0;
                });

                return (
                  <ResponsiveContainer width="100%" height="90%">
                    <LineChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                      <XAxis
                        dataKey="date"
                        tickFormatter={(value) => {
                          if (granularity === 'daily') return dayjs(value).format('MMM D');
                          if (granularity === 'weekly') return value;
                          return dayjs(value + '-01').format('MMM YY');
                        }}
                        stroke="rgba(255,255,255,0.5)"
                        fontSize={11}
                      />
                      <YAxis
                        yAxisId="left"
                        stroke="#8884d8"
                        fontSize={11}
                        tickFormatter={(value) => value >= 1000 ? `${(value / 1000).toFixed(0)}k` : value}
                      />
                      <YAxis yAxisId="right" orientation="right" stroke="#82ca9d" fontSize={11} />
                      <Tooltip
                        labelFormatter={(value) => {
                          if (granularity === 'daily') return dayjs(value).format('MMM D, YYYY');
                          return value;
                        }}
                        formatter={(value, name) => [value.toLocaleString(), name]}
                        contentStyle={{
                          backgroundColor: 'rgba(30, 30, 30, 0.95)',
                          border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: 4,
                        }}
                      />
                      <Legend
                        onClick={(e) => handleLegendClick(e.dataKey)}
                        wrapperStyle={{ cursor: 'pointer' }}
                        formatter={(value, entry) => (
                          <span style={{ color: hiddenLines[entry.dataKey] ? '#666' : entry.color, textDecoration: hiddenLines[entry.dataKey] ? 'line-through' : 'none' }}>
                            {value}
                          </span>
                        )}
                      />
                      {lines.map((line) => (
                        <Line
                          key={line.dataKey}
                          yAxisId={line.yAxisId}
                          type="monotone"
                          dataKey={line.dataKey}
                          name={line.name}
                          stroke={line.stroke}
                          strokeWidth={2}
                          dot={false}
                          activeDot={{ r: 6 }}
                          hide={hiddenLines[line.dataKey]}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                );
              })()
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80%' }}>
                <Typography sx={{
                  color: "text.secondary"
                }}>No trend data available</Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Cost Breakdown Pie Chart */}
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <Paper sx={{ p: 3, height: 400 }}>
            <Typography variant="subtitle1" gutterBottom sx={{
              color: "text.secondary"
            }}>
              Cost Distribution
            </Typography>
            {pieData.length > 0 && pieData[0].value > 0 ? (
              <ResponsiveContainer width="100%" height="90%">
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ name, percent }) =>
                      percent > 0 ? `${name} (${(percent * 100).toFixed(0)}%)` : ''
                    }
                  >
                    {pieData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80%' }}>
                <Typography sx={{
                  color: "text.secondary"
                }}>No costs recorded yet</Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Recent Room Activity */}
        <Grid size={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recent Room Activity
            </Typography>
            {activity?.activity?.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Room Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Participants</TableCell>
                      <TableCell>Started</TableCell>
                      <TableCell align="right">Duration (mins)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activity.activity.map((room) => (
                      <TableRow
                        key={room.id}
                        hover
                        onDoubleClick={() => navigate(`/rooms/${room.id}`)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>{room.name}</TableCell>
                        <TableCell>
                          <Chip
                            label={room.active ? 'Active' : 'Ended'}
                            color={room.active ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">{room.participants}</TableCell>
                        <TableCell>{dayjs(room.timeStarted).format('MMM D, HH:mm')}</TableCell>
                        <TableCell align="right">
                          {room.duration !== null ? room.duration : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography
                sx={{
                  color: "text.secondary",
                  py: 2,
                  textAlign: 'center'
                }}>
                No recent room activity
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default UsageDashboard;
